import { useState } from "react";
import Reveal from "@/components/base/Reveal";
import Badge from "@/components/base/Badge";
import Eyebrow from "@/components/base/Eyebrow";
import DetailModal from "@/components/base/DetailModal";
import { promotions } from "@/mocks/site";
import { buildWhatsAppLink } from "@/utils/links";

type PromoBadgeTone = "gold" | "rose" | "terracotta";

type Tone = {
  bar: string;
  halo: string;
  shadow: string;
  chip: PromoBadgeTone;
  priceBlock: string;
  priceText: string;
  labelText: string;
  strikeText: string;
  cta: string;
};

const toneMap: Record<string, Tone> = {
  gold: {
    bar: "bar-gold",
    halo: "glow-gold",
    shadow: "hover:shadow-gold-soft",
    chip: "gold",
    priceBlock: "price-gold border-accent-200",
    priceText: "text-foreground-950",
    labelText: "text-accent-800",
    strikeText: "text-foreground-400",
    cta: "cta-primary bg-primary-500 text-background-50 hover:bg-primary-600",
  },
  dark: {
    bar: "bar-gold",
    halo: "glow-lilac",
    shadow: "hover:shadow-gold-soft",
    chip: "gold",
    priceBlock: "border-primary-700 bg-gradient-to-br from-primary-600 to-primary-800",
    priceText: "text-background-50",
    labelText: "text-background-300",
    strikeText: "text-background-400",
    cta: "cta-gold",
  },
  warm: {
    bar: "bar-terracotta",
    halo: "glow-rose",
    shadow: "hover:shadow-rose-soft",
    chip: "terracotta",
    priceBlock: "price-blush border-secondary-200",
    priceText: "text-foreground-950",
    labelText: "text-[oklch(0.46_0.11_38)]",
    strikeText: "text-foreground-400",
    cta: "btn-promo",
  },
};

export default function PromotionsSection() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const active = activeIndex === null ? null : promotions[activeIndex];

  return (
    <section id="promos" className="relative overflow-hidden bg-background-100">
      <div className="gradient-blush pointer-events-none absolute inset-0 opacity-90" />
      <div className="pointer-events-none absolute -left-20 top-10 h-[380px] w-[380px] rounded-full glow-lilac blur-3xl" />
      <div className="pointer-events-none absolute -right-16 bottom-0 h-[420px] w-[420px] rounded-full glow-rose blur-3xl" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px line-gold" />

      <div className="relative mx-auto w-full max-w-[1400px] px-5 py-16 md:px-10 md:py-24">
        <Reveal className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <Eyebrow>Promociones ALÉA</Eyebrow>
            <h2 className="mt-4 font-heading text-4xl font-bold leading-[1.05] text-foreground-950 md:text-5xl">
              Belleza clínica, precio inteligente
            </h2>
          </div>
          <p className="max-w-md text-[15px] leading-relaxed text-foreground-600">
            Protocolos seleccionados con vigencia limitada. Toca una promoción para ver qué incluye y
            agendar tu lugar.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {promotions.map((promo, index) => {
            const tone = toneMap[promo.tone] ?? toneMap.gold;
            return (
              <Reveal key={promo.name} delay={index * 100} className="relative flex">
                <span
                  aria-hidden
                  className={`pointer-events-none absolute -inset-5 rounded-[44px] opacity-60 blur-2xl ${tone.halo}`}
                />
                <button
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  aria-label={`Ver promoción: ${promo.name}`}
                  className={`card-soft group relative flex w-full flex-col overflow-hidden rounded-[26px] border border-secondary-200 bg-background-50 text-left transition-all duration-500 hover:-translate-y-1 ${tone.shadow}`}
                >
                  <span className={`absolute left-0 top-0 z-10 h-full w-1 ${tone.bar}`} />

                  <div className="relative h-[320px] w-full overflow-hidden bg-background-100">
                    <img
                      src={promo.image}
                      alt={`${promo.name} — promoción ALÉA Aesthetic House`}
                      title={`${promo.name} promoción estética clínica Culiacán`}
                      className="h-full w-full object-cover object-top transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-primary-950/45 to-transparent" />
                    <Badge
                      tone={tone.chip}
                      icon="ri-flashlight-fill"
                      className="absolute left-5 top-4"
                    >
                      {promo.badge}
                    </Badge>
                  </div>

                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-heading text-xl font-bold leading-snug text-foreground-950">
                      {promo.name}
                    </h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">
                      {promo.tagline}
                    </p>

                    <div
                      className={`mt-5 flex items-end justify-between gap-3 rounded-[18px] border px-4 py-3.5 ${tone.priceBlock}`}
                    >
                      <div className="flex flex-col">
                        <span
                          className={`font-label text-[9px] font-semibold uppercase tracking-[0.18em] ${tone.labelText}`}
                        >
                          Precio promocional
                        </span>
                        <div className="mt-1 flex items-baseline gap-2">
                          <span
                            className={`font-heading text-[32px] font-extrabold leading-none ${tone.priceText}`}
                          >
                            {promo.promoPrice}
                          </span>
                          <span className={`text-sm line-through ${tone.strikeText}`}>
                            {promo.regularPrice}
                          </span>
                        </div>
                      </div>
                      <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-background-50/80 px-2.5 py-1 font-label text-[9px] font-semibold uppercase tracking-[0.12em] text-foreground-700">
                        <i className="ri-time-line text-[11px]" />
                        Limitada
                      </span>
                    </div>

                    <span
                      className={`mt-4 inline-flex min-h-[48px] items-center justify-center gap-2 whitespace-nowrap rounded-[16px] px-5 font-label text-[11px] font-semibold uppercase tracking-[0.14em] transition-all duration-300 ${tone.cta}`}
                    >
                      Ver promoción
                      <i className="ri-arrow-right-line text-base transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>

      <DetailModal
        open={active !== null}
        onClose={() => setActiveIndex(null)}
        unitLabel={`Promoción · ${active?.badge ?? ""}`}
        title={active?.name ?? ""}
        image={active?.image ?? ""}
        price={active?.promoPrice}
        strikePrice={active?.regularPrice}
        metaNote={active?.valid}
        description={active?.description ?? ""}
        includesTitle="Qué incluye"
        includes={active?.benefits}
        conditionsTitle="Condiciones"
        conditions={active?.conditions}
        ctaLabel="Agendar por WhatsApp"
        buildCtaHref={() =>
          buildWhatsAppLink(
            `Hola ALÉA, quiero aprovechar la promoción "${active?.name}" (${active?.promoPrice}).`
          )
        }
      />
    </section>
  );
}